import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  CircularProgress,
  IconButton,
  Tooltip,
  TablePagination,
  useTheme,
  useMediaQuery,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import VisibilityIcon from '@mui/icons-material/Visibility';
import BarChartIcon from '@mui/icons-material/BarChart';
import axios from '../../services/axiosConfig';
import { environment } from '../../config/environment';
import { useAuth } from '../../context/AuthContext';
import { ICoupon } from '../../types/coupon';
import { ICouponStats } from '../../types/couponStats';
import CouponEditDialog from './CouponEditDialog';
import StatsDialog from '../shared/StatsDialog';
import { couponService } from '../../services/couponService';

const CouponsList: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [coupons, setCoupons] = useState<ICoupon[]>([]);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [productCode, setProductCode] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedCoupon, setSelectedCoupon] = useState<ICoupon | null>(null);
  const [statsDialogOpen, setStatsDialogOpen] = useState(false);
  const [statsData, setStatsData] = useState<ICouponStats[]>([]);
  const [statsCoupon, setStatsCoupon] = useState<ICoupon | null>(null);
  const { hasRole } = useAuth();

  const loadCoupons = useCallback(async () => {
    setLoading(true);
    try {
      const params: any = {
        notification_type: 'coupon'
      };
      if (couponCode) params.couponCode = couponCode;
      if (productCode) params.productCode = productCode;

      const response = await axios.get(`${environment.apiUrl}coupon`, { params });
      console.log('Coupons response:', response.data);

      // Ensure response.data is an array
      const couponsData = Array.isArray(response.data) ? response.data : [];
      setCoupons(couponsData);
    } catch (error) {
      console.error('Error loading coupons:', error);
      setCoupons([]); // Set empty array on error
    } finally {
      setLoading(false);
    }
  }, [couponCode, productCode]);

  useEffect(() => {
    loadCoupons();
  }, [loadCoupons]);

  const handleSearch = () => {
    setPage(0);
    loadCoupons();
  };

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedCoupons = coupons.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  const handleHideCoupon = async (coupon: ICoupon) => {
    if (window.confirm('Are you sure you want to make this coupon INACTIVE?')) {
      try {
        await axios.delete(`${environment.apiUrl}coupon/${coupon.couponId}`);
        loadCoupons();
      } catch (error) {
        console.error('Error hiding coupon:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleUnhideCoupon = async (coupon: ICoupon) => {
    if (window.confirm('Are you sure you want to make this coupon ACTIVE again?')) {
      try {
        await axios.put(`${environment.apiUrl}coupon/${coupon.couponId}`);
        loadCoupons();
      } catch (error) {
        console.error('Error unhiding coupon:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const handleDeleteCoupon = async (coupon: ICoupon) => {
    if (window.confirm('Are you sure you want to delete this coupon permanently?')) {
      try {
        await axios.delete(`${environment.apiUrl}coupon/${coupon.couponId}?force=true`);
        loadCoupons();
      } catch (error) {
        console.error('Error deleting coupon:', error);
        alert('Network error. Try again later');
      }
    }
  };

  const showDeleteButton = (coupon: ICoupon) => {
    return coupon.isDeleted && hasRole(['coupon:admin', 'coupon:tester']);
  };

  const handleEditCoupon = (coupon: ICoupon) => {
    setSelectedCoupon(coupon);
    setEditDialogOpen(true);
  };

  const handleNewCoupon = () => {
    setSelectedCoupon(null);
    setEditDialogOpen(true);
  };

  const handleDialogClose = () => {
    setEditDialogOpen(false);
    setSelectedCoupon(null);
  };

  const handleCouponSaved = () => {
    loadCoupons();
  };

  const handleShowStats = async (coupon: ICoupon) => {
    try {
      setLoading(true);
      const stats = await couponService.getStats(coupon.couponId!, 'coupon');
      setStatsData(stats);
      setStatsCoupon(coupon);
      setStatsDialogOpen(true);
    } catch (error) {
      console.error('Error loading stats:', error);
      alert('Error loading statistics');
    } finally {
      setLoading(false);
    }
  };

  const handleStatsDialogClose = () => {
    setStatsDialogOpen(false);
    setStatsData([]);
    setStatsCoupon(null);
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Box 
        display="flex" 
        justifyContent="space-between" 
        alignItems="center" 
        mb={3}
        flexWrap="wrap"
        gap={2}
      >
        <Typography variant="h5" sx={{ fontWeight: 500, color: 'text.secondary' }}>
          Coupons
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={handleNewCoupon}
          sx={{ minWidth: '140px' }}
        >
          New Coupon
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} alignItems="flex-end" flexWrap="wrap">
          <TextField
            label="Coupon Code"
            value={couponCode}
            onChange={(e) => setCouponCode(e.target.value)}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: '150px' }}
          />
          <TextField
            label="Product Code"
            value={productCode}
            onChange={(e) => setProductCode(e.target.value)}
            size="small"
            sx={{ flex: '1 1 200px', minWidth: '150px' }}
          />
          <Button 
            variant="contained" 
            onClick={handleSearch}
            sx={{ minWidth: '120px', height: '40px' }}
          >
            Search
          </Button>
        </Box>
      </Paper>

      {loading ? (
        <Box display="flex" justifyContent="center" p={6}>
          <CircularProgress size={48} />
        </Box>
      ) : (
        <>
          {/* Mobile Card View */}
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {paginatedCoupons.map((coupon) => (
              <Card 
                key={coupon.couponId} 
                sx={{ 
                  mb: 2, 
                  border: coupon.isDeleted ? '1px solid' : 'none',
                  borderColor: coupon.isDeleted ? 'error.main' : 'transparent',
                  backgroundColor: coupon.isDeleted ? 'rgba(244, 67, 54, 0.08)' : 'background.paper'
                }}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="h6" sx={{ fontWeight: 600, color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.couponCode}
                    </Typography>
                    <Box display="flex" gap={0.5}>
                      <IconButton size="small" color="primary" onClick={() => handleEditCoupon(coupon)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" color="info" onClick={() => handleShowStats(coupon)}>
                        <BarChartIcon fontSize="small" />
                      </IconButton>
                      {!coupon.isDeleted ? (
                        <IconButton size="small" color="warning" onClick={() => handleHideCoupon(coupon)}>
                          <VisibilityOffIcon fontSize="small" />
                        </IconButton>
                      ) : (
                        <IconButton size="small" color="success" onClick={() => handleUnhideCoupon(coupon)}>
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      )}
                      {showDeleteButton(coupon) ? (
                        <IconButton size="small" color="error" onClick={() => handleDeleteCoupon(coupon)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                    </Box>
                  </Box>
                  <Divider sx={{ my: 1.5 }} />
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date Created:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {coupon.dateCreated && coupon.dateCreated !== undefined 
                          ? new Date(coupon.dateCreated).toLocaleDateString() 
                          : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date Start:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {coupon.dateStart ? new Date(coupon.dateStart).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Date End:</Typography>
                      <Typography variant="body2" fontWeight={500}>
                        {coupon.dateEnd ? new Date(coupon.dateEnd).toLocaleDateString() : '-'}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 0.5 }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Total:</Typography>
                      <Typography variant="body2" fontWeight={600}>{coupon.totalCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Sent:</Typography>
                      <Typography variant="body2" fontWeight={500}>{coupon.sentCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Received:</Typography>
                      <Typography variant="body2" fontWeight={500}>{coupon.receivedCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Viewed:</Typography>
                      <Typography variant="body2" fontWeight={500}>{coupon.viewedCount}</Typography>
                    </Box>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" color="text.secondary">Redeemed:</Typography>
                      <Typography variant="body2" fontWeight={500}>{coupon.redeemedCount}</Typography>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            ))}
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={coupons.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </Box>

          {/* Desktop Table View */}
          <Paper sx={{ borderRadius: 2, overflow: 'hidden', display: { xs: 'none', md: 'block' } }}>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: 650, tableLayout: 'auto' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Coupon Code</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 100 }}>Date Created</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 100 }}>Date Start</TableCell>
                    <TableCell sx={{ fontWeight: 600, display: { xs: 'none', lg: 'table-cell' }, minWidth: 100 }}>Date End</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Total</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Sent</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Received</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 70 }}>Viewed</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 80 }}>Redeemed</TableCell>
                    <TableCell sx={{ fontWeight: 600, minWidth: 120 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {paginatedCoupons.map((coupon) => (
                  <TableRow
                    key={coupon.couponId}
                    sx={{ 
                      color: coupon.isDeleted ? 'error.main' : 'inherit',
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit', fontWeight: coupon.isDeleted ? 500 : 400, wordBreak: 'break-word' }}>
                      {coupon.couponCode}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.dateCreated && coupon.dateCreated !== undefined 
                        ? new Date(coupon.dateCreated).toLocaleDateString() 
                        : '-'}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit', display: { xs: 'none', lg: 'table-cell' } }}>
                      {coupon.dateStart ? new Date(coupon.dateStart).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit', display: { xs: 'none', lg: 'table-cell' } }}>
                      {coupon.dateEnd ? new Date(coupon.dateEnd).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.totalCount}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.sentCount}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.receivedCount}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.viewedCount}
                    </TableCell>
                    <TableCell sx={{ color: coupon.isDeleted ? 'error.main' : 'inherit' }}>
                      {coupon.redeemedCount}
                    </TableCell>
                    <TableCell>
                      <Box display="flex" gap={1}>
                        <Tooltip title="Edit">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleEditCoupon(coupon)}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        <Tooltip title="Statistics">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleShowStats(coupon)}
                          >
                            <BarChartIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>

                        {!coupon.isDeleted ? (
                          <Tooltip title="Make Inactive">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleHideCoupon(coupon)}
                            >
                              <VisibilityOffIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Make Active">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleUnhideCoupon(coupon)}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {showDeleteButton(coupon) ? (
                          <Tooltip title="Delete Permanently">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteCoupon(coupon)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : null}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={coupons.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              sx={{ borderTop: '1px solid', borderColor: 'divider' }}
            />
          </TableContainer>
          </Paper>
        </>
      )}

      <CouponEditDialog
        open={editDialogOpen}
        onClose={handleDialogClose}
        coupon={selectedCoupon}
        onSaved={handleCouponSaved}
      />

      <StatsDialog
        open={statsDialogOpen}
        onClose={handleStatsDialogClose}
        coupon={statsCoupon}
        stats={statsData}
      />
    </Box>
  );
};

export default CouponsList;
